import React from 'react';
import useInView from '../../hooks/useInView';
import { Box, Typography, Grid } from '@mui/material';
import { FONT_FAMILY } from '../../theme';
import Slider from 'react-slick';

export default function GallerySection({ images = [], title = 'Photo Gallery', description = 'A relaxing environment offering flavorful iced hookahs, rich espresso and coffee, delicious teas, chill music, a fun and friendly staff and plenty of comfy couches to relax in.' }) {
    const getRandom = (arr, count = 4) => {
        const a = [...arr];
        for (let i = a.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [a[i], a[j]] = [a[j], a[i]];
        }
        return a.slice(0, count);
    };
    // Show a small selection (3) to keep the gallery compact
    const randomGalleryImages = getRandom(images, 3);
    const [ref, inView] = useInView({ threshold: 0.12 });
    const sliderSettings = { dots: true, infinite: true, speed: 500, slidesToShow: 1, slidesToScroll: 1, arrows: false, adaptiveHeight: true };

    return (
        <Box ref={ref} sx={{ mb: 0, pt: 6, pb: 6, px: 0, bgcolor: '#f8f2ea', width: '100%', borderRadius: 0, mx: 0 }}>
            <Typography
                variant="h3"
                align="center"
                sx={{ fontWeight: 700, color: '#222', letterSpacing: 1, mb: 2, fontFamily: FONT_FAMILY }}
                style={{
                    opacity: inView ? 1 : 0,
                    transform: inView ? 'translateY(0)' : 'translateY(18px)',
                    transition: 'opacity 700ms ease, transform 700ms ease'
                }}
            >
                {title}
            </Typography>
            <Box sx={{ width: 80, height: 6, bgcolor: '#ed5034', borderRadius: 2, mb: 3, mx: 'auto' }} />
            <Typography
                align="center"
                sx={{ mb: 4, color: '#a6a6a6', fontSize: 18, maxWidth: 900, mx: 'auto', fontFamily: FONT_FAMILY }}
                style={{
                    opacity: inView ? 1 : 0,
                    transform: inView ? 'translateY(0)' : 'translateY(18px)',
                    transition: 'opacity 800ms ease 80ms, transform 800ms ease 80ms'
                }}
            >
                {description}
            </Typography>

            <Box ref={ref} sx={{ display: { xs: 'block', md: 'none' }, width: '100%', maxWidth: 400, mx: 'auto' }}>
                <Slider {...sliderSettings}>
                    {randomGalleryImages.map((src, idx) => (
                        <Box key={idx} sx={{
                            width: '100%', height: 220, borderRadius: 8, boxShadow: 0, px: 1,
                            overflow: 'hidden',
                            transition: 'transform 420ms cubic-bezier(.2,.9,.2,1), box-shadow 420ms',
                            '&:hover img': { transform: 'translateY(-8px) scale(1.03)', boxShadow: '0 26px 60px rgba(0,0,0,0.22)' }
                        }}>
                            <img
                                src={src}
                                alt={`Gallery ${idx + 1}`}
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                    borderRadius: '32px',
                                    opacity: inView ? 1 : 0,
                                    transform: inView ? 'translateY(0)' : 'translateY(18px)',
                                    transition: 'opacity 600ms ease, transform 600ms ease',
                                }}
                            />
                        </Box>
                    ))}
                </Slider>
            </Box>
            {/* Masonry-like gallery for desktop using CSS columns for a broken layout */}
            <Box sx={{ display: { xs: 'none', md: 'block' }, px: { xs: 0, md: 6 } }}>
                <Box
                    sx={{
                        columnCount: { md: 3 },
                        columnGap: '20px',
                        '& img': {
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            borderRadius: 2,
                            marginBottom: '20px',
                            transition: 'transform 0.35s ease, box-shadow 0.35s ease',
                        },
                        '& figure': {
                            breakInside: 'avoid',
                            WebkitColumnBreakInside: 'avoid',
                            MozColumnBreakInside: 'avoid',
                            margin: 0,
                        }
                    }}
                >
                    {randomGalleryImages.map((src, idx) => (
                        <figure key={idx} style={{ position: 'relative' }}>
                            <a href={src} target="_blank" rel="noopener noreferrer" style={{ display: 'block' }}>
                                <img
                                    src={src}
                                    alt={`Gallery ${idx + 1}`}
                                    style={{
                                        borderRadius: '12px',
                                        boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
                                        opacity: inView ? 1 : 0,
                                        transform: inView ? 'translateY(0)' : 'translateY(18px)',
                                        transition: `opacity 600ms ease ${idx * 120}ms, transform 600ms ease ${idx * 120}ms`,
                                        display: 'block',
                                    }}
                                />
                            </a>
                        </figure>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
